import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogClose,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { ImageWithFallback } from "@/components/ui/image-with-fallback";
import { cn } from "@/lib/utils";
import { useState } from "react";
import type { LaudoPeca } from "@shared/schema";
import { destinacaoInicialRt } from "@shared/laudo-checklist";
import {
  DESTINACAO_LABELS,
  DESTINACAO_COLORS,
  VEICULO_TIPO_LABELS,
} from "@/lib/constants";

interface EditarDestinacaoProps {
  laudoPecas: LaudoPeca[];
  /** Flag congelada do laudo: com o módulo Detran, peça sem foto não vira Inexistente sozinha. */
  moduloDetran: boolean;
  /** Devolve false quando o servidor recusou: o diálogo fica aberto. */
  onSave: (updates: { id: string; destinacao: string }[]) => Promise<boolean>;
  isLoading?: boolean;
}

export function EditarDestinacao({
  laudoPecas,
  moduloDetran,
  onSave,
  isLoading = false,
}: EditarDestinacaoProps) {
  const calcularDestinacoes = (pecas: LaudoPeca[]) => {
    const initial: Record<string, string> = {};
    pecas.forEach((peca) => {
      initial[peca.id] = destinacaoInicialRt(peca, moduloDetran);
    });
    return initial;
  };

  const [localDestinacoes, setLocalDestinacoes] = useState<
    Record<string, string>
  >(() => calcularDestinacoes(laudoPecas));
  const [open, setOpen] = useState(false);
  const [imagemAberta, setImagemAberta] = useState<string | null>(null);

  const handleOpenChange = (isOpen: boolean) => {
    if (isOpen) {
      // Recalcula com as props mais recentes (ex: fotoUrl pode ter chegado após o mount)
      setLocalDestinacoes(calcularDestinacoes(laudoPecas));
    }
    setOpen(isOpen);
  };

  const handleDestinacaoChange = (pecaId: string, novaDest: string) => {
    setLocalDestinacoes((prev) => ({ ...prev, [pecaId]: novaDest }));
  };

  const handleSave = async () => {
    const updates = laudoPecas.map((peca) => ({
      id: peca.id,
      destinacao: localDestinacoes[peca.id] || "inexistente",
    }));
    if (await onSave(updates)) setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">
          Dar Destinação
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Designar Destinação às Peças</DialogTitle>
          <DialogDescription>
            Selecione a destinação para cada peça do laudo
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {laudoPecas.map((peca) => (
            <Card key={peca.id}>
              <CardContent className="p-4">
                <div className="flex flex-col gap-3">
                  <div>
                    <p className="font-semibold text-slate-900">{peca.nome}</p>
                    <p className="text-sm text-slate-500">
                      {peca.uf} -
                      {VEICULO_TIPO_LABELS[peca.tipoVeiculo] ||
                        peca.tipoVeiculo}
                    </p>
                  </div>

                  <ImageWithFallback
                    src={peca.fotoUrl}
                    alt={peca.nome}
                    containerClassName="w-full h-24 rounded-md cursor-pointer hover:opacity-80 transition-opacity"
                    className="w-full h-24 object-cover rounded-md"
                    onClick={() => setImagemAberta(peca.fotoUrl)}
                  />

                  {peca.observacaoParceiro && <p className="text-sm text-slate-600 whitespace-pre-wrap break-words">
                    <span className="font-medium">Observação do Parceiro de Negócios: </span>{peca.observacaoParceiro}
                  </p>}

                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-2 block">
                      Destinação
                    </label>
                    <Select
                      value={localDestinacoes[peca.id] || ""}
                      onValueChange={(value) =>
                        handleDestinacaoChange(peca.id, value)
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(DESTINACAO_LABELS).map(
                          ([key, label]) => (
                            <SelectItem key={key} value={key}>
                              {label}
                            </SelectItem>
                          ),
                        )}
                      </SelectContent>
                    </Select>
                  </div>

                  {localDestinacoes[peca.id] && (
                    <Badge
                      className={cn(
                        "w-fit",
                        DESTINACAO_COLORS[localDestinacoes[peca.id]],
                      )}
                    >
                      {DESTINACAO_LABELS[localDestinacoes[peca.id]]}
                    </Badge>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="flex justify-end gap-3 pt-4 border-t">
          <Button variant="outline" onClick={() => setOpen(false)}>
            Cancelar
          </Button>
          <Button onClick={handleSave} disabled={isLoading}>
            {isLoading ? "Salvando..." : "Salvar Destinações"}
          </Button>
        </div>

        {/* Modal de Imagem */}
        <Dialog
          open={!!imagemAberta}
          onOpenChange={() => setImagemAberta(null)}
        >
          <DialogContent className="max-w-4xl max-h-[90vh] p-0 bg-black border-0">
            <div className="flex items-center justify-center w-full h-full">
              <img
                src={imagemAberta || ""}
                alt="Imagem ampliada"
                className="max-w-full max-h-[85vh] object-contain"
              />
            </div>
            <DialogClose className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-0 transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-0 disabled:pointer-events-none data-[state=open]:bg-transparent data-[state=open]:text-white">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </DialogClose>
          </DialogContent>
        </Dialog>
      </DialogContent>
    </Dialog>
  );
}
